import { FocusTrap, createFocusTrap } from 'focus-trap';
import { PropertyValueMap, TemplateResult, html, CSSResultGroup } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { DirectiveResult } from 'lit/directive';
import { ClassMapDirective } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined.js';
import { nanoid } from 'nanoid';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksNavSelectEvent } from '../../events/blocks-nav-select';
import { KEYS } from '../../util/event-keys';
import { BlocksElement } from '../BlocksElement';
import '../dropdown-panel/dropdown-panel';
import { BlocksGrid } from '../grid/grid';
import '../icon/icons/chevron-down';
import { BlocksListItem } from '../list-item/list-item';
import { BlocksList } from '../list/list';
import type { LinkTarget, PrimaryNavItemAlignment, PrimaryNavItemVariant } from './primary-nav-item.model';
import styles from './primary-nav-item.styles';

interface Items {
  id: string;
  level: string;
  width: number;
}

/**
 * @deprecated - This component `cat-primary-nav-item` has been deprecated and the updated version will be available with name `cat-primary-nav-item-labs`. New component will be found under labs folder from Minor Release 3.10.0, `cat-primary-nav-item` will be removed in 5.0 major release.
 * @slot default  - The label for the navigation item
 * @slot before - Content appearing before the default slot
 * @slot icon - Content appearing after the before slot
 * @fires bl-nav-select - emitted along with `isMobile` prop when nav-item is selected
 * @csspart base - The component's base wrapper.
 * @csspart link - The component's link.
 * @csspart icon-before - The component's icon-before.
 * @csspart part-before - The component's before slot.
 * @csspart icon-after - The component's icon-after.
 * @csspart part-after - The component's after slot.
 * @csspart part-icon - The component's icon slot.
 * @csspart icon-down - The component's `<cat-icon-chevron-down>` icon.
 * @csspart panel - The component's panel wrapper.
 * @csspart panel-inner - The component's panel-inner.
 * @csspart part-default - The component's default slot.
 * @csspart part-custom-link - The component's custom link slot
 * @csspart dropdown-panel - The component's dropdown-panel element
 */
export class BlocksPrimaryNavItem extends BlocksElement {
  /**
   * Primary nav item text
   */
  @property({ type: String })
  text: string | undefined;

  /*
   * Target to display the linked URL.
   *_self: the current browsing context.(Default)
   *_blank: opens the linked url in new tab.
   *_parent: the parent browsing context of the current one. If no parent, behaves as _self.
   *_top: the topmost browsing context (the "highest" context that's an ancestor of the current one). If no ancestors, behaves as _self.
   */
  @property({ type: String })
  target: LinkTarget;

  /**
   * Primary nav item href
   */
  @property({ type: String })
  href: string | undefined;

  /**
   * Variant to set behavior for primary NavItem
   * -megamenu: To set behavior megamneu
   * -steppedmegamenu: To set behavior stepped megamenu
   */
  @property({ type: String })
  variant: PrimaryNavItemVariant = 'default';

  /**
   * Turns default dropdown on
   */
  @property({ type: Boolean })
  dropdown: boolean | undefined;

  /**
   * Turns default dropdown on
   */
  @property({ type: String })
  dropdownAlign: PrimaryNavItemAlignment;

  /**
   * More dropdown class addition
   * 1) Allows for better styling
   */
  @property({ type: Boolean })
  more: boolean | undefined;

  /**
   * Append to the class name. Used for passing in utility classes
   */
  @property({ type: Boolean })
  isActive: boolean | undefined;

  /**
   * Current state added to represent the current page or parent location in product
   */
  @property({ type: Boolean })
  isCurrent: boolean | undefined;

  @state()
  isMobile = false;

  private get megaMenu() {
    return this.variant === 'megamenu' || this.variant === 'steppedmegamenu';
  }

  private get hasCustomLinkSlot(): boolean {
    return this.slots.hasSlotted('custom-link');
  }

  /**
   * To query the navlink
   */
  @query('.cat-c-primary-nav__item.cat-is-active > .cat-c-primary-nav__link')
  _navLink: HTMLButtonElement | HTMLAnchorElement | undefined;

  /**
   * To query the navItem
   */
  @query('.cat-c-primary-nav__link')
  private _navItem: HTMLButtonElement | HTMLAnchorElement | undefined;

  /**
   * To query primary nav item panel
   */
  @query('.cat-c-primary-nav__item-panel')
  _megamenuPanel!: HTMLElement;

  private isItemSelected = false;

  private _focusTrap?: FocusTrap;
  private items: Items[] = [];

  public slots = new SlotController(this, 'before', 'after', 'custom-link', 'icon', null);

  /**
   * isFocusedIn - To specify whether the focus is inside the dropdown panel
   */
  private isFocusedIn: boolean | undefined;static styles: CSSResultGroup = styles;

  /**
   * Connected Callback lifecycle
   * 1) Pause/Unpause focustrap on contextual menu open and close
   * 2) Pause/Unpause focustrap on dropdown panel open and close
   * 3) while dynamic loading of stepped megamenu render should happen smoothly with updated data
   */
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('mousedown', this.handleOnClickOutside, false);
    if (this.megaMenu) {
      this.addEventListener('bl-contextual-menu-open', () => {
        this._focusTrap?.pause(); /* 1 */
      });

      this.addEventListener('bl-contextual-menu-close', () => {
        this._focusTrap?.unpause(); /* 1 */
      });
      /* 2 */
      this.addEventListener('bl-dropdown-open', this.pauseFocusTrap, true);
      this.addEventListener('bl-dropdown-close', this.unPauseFocusTrap, true);
    }
    this.addEventListener('bl-set-dynamic-width', this.dynamicWidthHandler as EventListener);

    /* 3 */
    this.addEventListener('bl-list-slot-change', () => {
      this.requestUpdate();
    });
  }

  /**
   * Disconnected callback lifecycle
   * 1) Remove window resize event listener
   * 2) Remove the dropdown panel open and close event listener
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('mousedown', this.handleOnClickOutside, false);
    this._focusTrap?.deactivate();
    this.removeEventListener('bl-dropdown-open', this.pauseFocusTrap, true);
    this.removeEventListener('bl-dropdown-close', this.unPauseFocusTrap, true);
    this.removeEventListener('bl-set-dynamic-width', this.dynamicWidthHandler as EventListener);
  }

  /**
   * 1. This will set ariaposinset (itemCount) and ariasetsize (totalCount) for listItems in MegaMenu
   */
  private setRoles() {
    const isMegaMenu = this.variant === 'megamenu';
    if (this.more || this.dropdown || isMegaMenu || this.variant === 'steppedmegamenu') {
      const grids = isMegaMenu ? Array.from(this.querySelectorAll('cat-grid')) : [];
      const listItem = this.getCatList()?.querySelector<BlocksListItem>('cat-list-item');
      let listItems = grids.length === 0 ? Array.from(this.getCatListItems) : [];
      const list = Array.from(this.querySelectorAll('cat-list'));

      list.forEach((item: BlocksList) => {
        item.listRole = 'menu';
      });

      grids.forEach((grid: BlocksGrid) => {
        const megaMenuListItems = grid.querySelectorAll('cat-list-item');
        const totalItems = megaMenuListItems.length.toString();

        // 1
        megaMenuListItems.forEach((item: BlocksListItem, index: number) => {
          const itemCount = (index + 1).toString();

          item.listItemRole = 'menuitem';
          item.ariaposinset = itemCount;
          item.ariasetsize = totalItems;
        });
      });

      listItems.forEach((item: BlocksListItem, index: number) => {
        item.listItemRole = 'menuitem';

        if (isMegaMenu) {
          item.ariaposinset = (index + 1).toString();
          item.ariasetsize = listItems.length.toString();
        }
      });
      if (this.dropdown && listItem && listItem.behavior === 'flyout') {
        listItem.crossAxisGap = -9;
      }
    }
  }

  protected async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    if (_changedProperties.has('isActive') && this.isActive && this.megaMenu && !this.isMobile) {
      this.activateFocusTrap();
    }

    if (_changedProperties.has('isMobile')) {
      this.resetSteppedMenuPanelWidth();
      this.updateListForSteppedMegamenu();
    }
  }

  override focus(): void {
    this._navItem?.focus();
  }

  /**
   *  Pause the focustrap
   */
  private pauseFocusTrap() {
    this._focusTrap?.pause();
  }

  /**
   *  Unpause the focustrap
   */
  private unPauseFocusTrap() {
    this._focusTrap?.unpause();
  }

  /**
   * Handle click outside the component
   * 1) Close the show hide panel on click outside
   * 2) If the nav is already closed then we don't care about outside clicks and we
   * can bail early
   * 3) By the time a user clicks on the page the shadowRoot will almost certainly be
   * defined, but TypeScript isn't that trusting and sees this.shadowRoot as possibly
   * undefined. To work around that we'll check that we have a shadowRoot (and a
   * rendered .host) element here to appease the TypeScript compiler. This should never
   * actually be shown or run for a human end user.
   * 4) Check to see if we clicked inside the active navigation item
   * 5) If the navigation is active and we've clicked outside of the nav then it should
   * be closed.
   * 6) Only apply click outside breakpoint greater than or equal to 768px to get better
   * accordion behavior on small screens
   */
  private handleOnClickOutside = (event: MouseEvent) => {
    /* 2 */
    if (!this.isActive) {
      return;
    }

    /* 3 */
    if (!this.shadowRoot?.host) {
      throw Error('Could not determine navigation context during click handler');
    }

    /* 4 */
    const didClickInside = event.composedPath().includes(this.shadowRoot.host);

    if (!this.isMobile) {
      /* 6 */
      /* 5 */
      if (this.isActive && !didClickInside) {
        this.closePanel();
      }
    }
  };

  /**
   * Toggle active state of primary nav item
   * 1) Remove isActive state from all sibling elements
   * 2) Toggle active state of element selected
   */
  private clickHandler = async (e: MouseEvent) => {
    e.preventDefault();
    if (this.parentNode) {
      const navItems = this.parentNode.querySelectorAll('cat-primary-nav-item');
      navItems.forEach((element) => {
        if (element !== this) {
          element.isActive = false; /* 1 */
        }
      });
    }
    this.isActive = !this.isActive; /* 2 */
    await this.updateComplete;
    if (this.isActive) {
      this.panelOpenEvent();
    } else {
      this.panelCloseEvent();
    }
    this.isItemSelected = false;
    if (this.isActive) {
      this.focus();
    }
  };

  /**
   * To handle dynamic width part
   */
  private dynamicWidthHandler(event: CustomEvent<{ activeElement: BlocksListItem }>) {
    this.setPanelWidthDynamically(event.detail.activeElement);
  }
  /**
   * Close primary nav panel
   */
  private closePanel() {
    this.isActive = false;
    this.isItemSelected = false;
    this._focusTrap?.deactivate();
    this.panelCloseEvent();
  }

  private activateFocusTrap() {
    this._focusTrap?.deactivate();
    const megaMenuWindow = this.shadowRoot?.querySelector<HTMLElement>('.cat-c-primary-nav__item-panel')!;
    megaMenuWindow?.focus();
    /** calling function to create focus trap */
    this._focusTrap = createFocusTrap(megaMenuWindow, {
      onActivate: () => megaMenuWindow.classList.add('is-active'),
      onDeactivate: () => megaMenuWindow.classList.remove('is-active'),
      initialFocus: false,
      preventScroll: true,
      tabbableOptions: { getShadowRoot: true },
      allowOutsideClick: true,
      fallbackFocus: this
    });
    /**To activate focus trap */
    this._focusTrap.activate();
  }

  /**
   * Handle on megaMenu keydown
   * 1) If a user strikes the escape key when the panel is open, close the panel
   * 2) Return focus back to the nav link
   */
  private handleOnMegaMenuKeyDown(e: KeyboardEvent) {
    if (e.key === KEYS.ESCAPE && this.isActive) {
      /* 1 */
      e.preventDefault();
      this.closePanel();
      this._navLink?.focus(); /* 2 */
    }
    const catListItems = this.getCatListItems;
    /**
     * This condition is used to check for Tab press after clicking the nav item.
     * In safari, clicking the nav item doesn't invoke the focus-visible pseudo selector,
     * therefore focusing manually
     * https://github.com/shoelace-style/shoelace/issues/2136
     */
    if (e.key === KEYS.TAB && this.isActive && !this.isFocusedIn) {
      if (catListItems.length > 0) {
        catListItems[0].focusOnFirstItem(e);
        this.isFocusedIn = true;
      }
    }
    for (let list of Array.from(catListItems)) {
      if (!(this.variant === 'steppedmegamenu')) {
        list.isArrowNav = false;
      }
    }
  }

  private handleOnKeyDown(e: KeyboardEvent) {
    if (e.key === KEYS.SPACE) {
      this.linkClickHandler(e);
    }
  }

  /**
   * Handle on dropdown keydown
   * 1) If a user strikes the escape key when the panel is open, close the panel
   * 2) Return focus back to the nav link
   * 3) Find the last/first item in the list. Set the last/first element to that item to define variable
   */
  private handleOnDropdownKeyDown(e: KeyboardEvent) {
    if (this.isActive === true) {
      const activeTagName = this.tagName.toUpperCase();
      if (e.key === KEYS.ESCAPE || e.key === KEYS.TAB) {
        /* 1 */
        e.preventDefault();
        this.closePanel();
        this._navLink?.focus(); /* 2 */
      } else if (activeTagName !== 'CAT-LIST-ITEM') {
        const catListItems = this.getCatListItems;
        if (catListItems.length > 0) {
          if (e.key === KEYS.DOWN_ARROW && !this.isItemSelected) {
            catListItems[0].focusOnFirstItem(e);
            this.isItemSelected = true;
          } else if (e.key === KEYS.UP_ARROW && !this.isItemSelected) {
            catListItems[0].focusOnLastItem(e);
            this.isItemSelected = true;
          }
        }
      }
    }
  }

  /**
   * Handle on keydown/click
   * 1) If key selected is enter, navigate to the custom link
   */

  private handleKeyDown(e: KeyboardEvent) {
    if (this.hasCustomLinkSlot) {
      if (e.key === KEYS.ENTER) {
        this._children?.[0].click();
      }
    }
  }

  /**
   * Click on list item without dropdown
   * 1) Check if the event gets cancelled to apply custom handler for setting isCurrent
   * 2) On click, remove all isCurrent properties from all primary nav items
   * 3) Add isCurrent to the item clicked
   * 4) Use this custom event at mobile view to toggle primary Nav overly
   * 5) If key selected is click, navigate to the custom link
   * @ignore
   */
  public linkClickHandler(e?: MouseEvent | KeyboardEvent | null) {
    if (this.hasCustomLinkSlot) {
      if (e?.type === 'click') {
        this._children?.[0].click(); /* 4 */
      }
    }

    const preventDefault = this.emitNavSelect();
    /* 1 */
    if (preventDefault) {
      return;
    }

    const navItems = this.parentNode?.querySelectorAll('cat-primary-nav-item');
    const moreItem = this.parentElement?.shadowRoot?.querySelector('cat-primary-nav-item');

    navItems?.forEach((element) => {
      if (element !== this) {
        element.isCurrent = false; /* 2 */
        if (moreItem?.isCurrent) {
          moreItem.isCurrent = false;
        }
      }

      const allListItems = element.querySelectorAll('cat-list-item');
      allListItems.forEach((item) => {
        if (item.isCurrent === true) {
          item.isCurrent = false; /* 2 */
        }
      });
      const moreListItems = moreItem?.querySelectorAll('cat-list-item');
      if (moreListItems) {
        moreListItems.forEach((item) => {
          if (item.isCurrent === true) {
            item.isCurrent = false; /* 2 */
          }
        });
      }
    });

    this.isCurrent = true;
  }

  /** Check if cat-list is in a slot and set the catList var */
  private getCatList(): BlocksList | null {
    return this.querySelector<BlocksList>('cat-list');
  }

  /**
   * Query all the cat-list-items
   */
  private get getCatListItems() {
    return Array.from(this.getCatList()?.querySelectorAll<BlocksListItem>('cat-list-item') ?? []);
  }

  /**
   * Method updates the panel width dynamically based on number of list details shown
   * @param subListElement - current list item selected
   */
  private setPanelWidthDynamically(subListElement: BlocksListItem) {
    const selectedItem = subListElement.shadowRoot?.querySelector('.cat-c-list-item__dropdown-panel');
    // Get width of panel
    let panelWidth = this._megamenuPanel.getBoundingClientRect().width;
    const listItems = this.getCatListItems;

    if (selectedItem) {
      // Get width of panel and selected list

      requestAnimationFrame(() => {
        let listWidth = selectedItem.getBoundingClientRect().width;

        // Get list Item Id and level details
        const level = subListElement.dataset.level!;
        const itemId = subListElement.dataset.listid!;

        // check level exist and check item exist
        const isLevelExist = this.items.some((item: { id: string; level: string; width: number }) => item.level === level);
        const isItemExist = this.items.some((item: { id: string; level: string; width: number }) => item.id === itemId && item.level === level);

        if (!isLevelExist) {
          // if level does not exist case
          this.pushTrackingItem({ id: itemId, level: level, width: listWidth, label: subListElement.innerText });
          // update the panel width
          this._megamenuPanel.style.width = `${panelWidth + listWidth}px`;
        } else {
          if (isLevelExist && isItemExist) {
            panelWidth = this.getPanelWidthIfLevelAndItemExist(level, listItems, panelWidth);
          } else if (isLevelExist && !isItemExist) {
            panelWidth = this.getPanelWidthIfLevelAndItemNotExist(level, listItems, panelWidth, listWidth);
            this.pushTrackingItem({ id: itemId, level: level, width: listWidth, label: subListElement.innerText });
          }
          this._megamenuPanel.style.width = `${panelWidth}px`;
        }
      });
    } else {
      panelWidth = this.getPanelWidthForNonSubItems(listItems, panelWidth);
      this._megamenuPanel.style.width = panelWidth > 0 ? `${panelWidth}px` : 'auto';
    }
  }

  /**
   * Gets panel width for non sub items for mega menu items
   * @param listItems current list elements
   * @param panelWidth  current panel width
   * @returns updated panel width
   */
  private getPanelWidthForNonSubItems(listItems: BlocksListItem[], panelWidth: number): number {
    this.items.forEach((item: { id: string; level: string; width: number }) => {
      this.updateActiveList(listItems, item);
      panelWidth = panelWidth - item.width;
      this.removeTrackingElement(item.id);
    });
    return panelWidth;
  }

  /**
   * Gets panel width for level and item exist in item array
   * @param level current level details
   * @param listItems current list elements
   * @param panelWidth current panel width
   * @returns updated panel width
   */
  private getPanelWidthIfLevelAndItemExist(level: string, listItems: BlocksListItem[], panelWidth: number): number {
    this.items
      .slice()
      .reverse()
      .forEach((item: { id: string; level: string; width: number }) => {
        if (level <= item.level) {
          this.updateActiveList(listItems, item);
          panelWidth = panelWidth - item.width;
          this.removeTrackingElement(item.id);
        }
      });
    return panelWidth;
  }

  /**
   * Gets panel width for level exist and item does not exist in item array
   * @param level current level details
   * @param listItemscurrent current list elements
   * @param panelWidth  current panel width
   * @param listWidth current list width
   * @returns updated panel width
   */
  private getPanelWidthIfLevelAndItemNotExist(level: string, listItems: BlocksListItem[], panelWidth: number, listWidth: number): number {
    this.items
      .slice()
      .reverse()
      .forEach((item: { id: string; level: string; width: number }) => {
        if (level === item.level) {
          this.updateActiveList(listItems, item);
          panelWidth = panelWidth - item.width;
          this.removeTrackingElement(item.id);
          panelWidth = panelWidth + listWidth;
        } else if (level < item.level) {
          this.updateActiveList(listItems, item);
          panelWidth = panelWidth - item.width;
          this.removeTrackingElement(item.id);
        }
      });
    return panelWidth;
  }

  /**
   * Makes the current active cat list to in active based on item id for stepped menu
   * @param activeListItems Current Active Lists
   * @param item Current Items in state
   */
  private updateActiveList(activeListItems: BlocksListItem[], item: { id: string; level: string; width: number }): void {
    const activeListIndex = activeListItems.findIndex((listItem) => listItem.dataset.listid === item.id && listItem.isActive);
    if (activeListIndex > 0) {
      activeListItems[activeListIndex].isActive = false;
    }
  }

  /**
   * List item selected from the primary nav item panel
   * 1) Check if the event gets cancelled to apply custom handler for setting isCurrent
   * 2) Get the event target. If that is the `cat-list-item` or the parent is (for spans inside),
   * then remove all isCurrent states from nav items including moreItem (since that is baked into the
   * primary nav component)
   * 3) Set the selected item to true
   * 4) If the item has children, then remove all `isCurrent` states from those items if another item is selected
   * 5) If nested children within dropdown, find all parent node list items and set `isCurrent` if that item is selected.
   */
  private itemSelected(evt: MouseEvent) {
    const subListElement = evt.target as BlocksListItem;
    if (subListElement.isStatic || subListElement.disabled) {
      return;
    }
    const allPrimaryNavItems = this.parentNode?.querySelectorAll('cat-primary-nav-item');

    if (this.variant === 'steppedmegamenu' && !this.isMobile) {
      const target = evt.target as HTMLElement;
      if (target.tagName.toLowerCase() !== 'cat-list-item') {
        return;
      }
      this.setPanelWidthDynamically(subListElement);
    }

    if (
      ((subListElement.outerHTML.includes('cat-list-item') && !subListElement.querySelector('cat-list')) ||
        (subListElement.parentElement?.outerHTML.includes('cat-list-item') && !subListElement.parentElement.querySelector('cat-list'))) &&
      !subListElement.disabled &&
      !subListElement.querySelector('cat-list')
    ) {
      const defaultPrevented = this.emitNavSelect();
      if (!defaultPrevented) {
        /* 1 */
        const navItems = this.parentNode?.querySelectorAll('cat-primary-nav-item');
        navItems?.forEach((element) => {
          /* 1 */
          if (element !== this) {
            const moreItem = element.parentElement?.shadowRoot?.querySelector<BlocksPrimaryNavItem>('cat-primary-nav-item');
            if (moreItem?.isCurrent) {
              moreItem.isCurrent = false; /* 2 */
            }
            element.isCurrent = false;
          } else if (element.classList.contains('cat-c-primary-nav__more')) {
            const allNavItems = this.parentElement?.parentElement?.querySelector('slot')?.assignedElements() as BlocksPrimaryNavItem[];
            allNavItems.forEach((item: BlocksPrimaryNavItem) => {
              item.isCurrent = false; /* 2 */
            });

            this.checkPrimaryNavItems();
          }
        });

        this.isCurrent = true; /* 3 */
        allPrimaryNavItems?.forEach((element) => {
          const allListItems = element.querySelectorAll<BlocksListItem>('cat-list-item');

          allListItems.forEach((item) => {
            if (item.isCurrent === true) {
              item.isCurrent = false; /* 4 */
            }
          });
        });
        if (subListElement.closest('cat-list-item')) {
          const selectedItem = <BlocksListItem>subListElement.closest('cat-list-item');
          selectedItem.isCurrent = true;
          let newSubListElement = selectedItem as BlocksListItem;

          while (newSubListElement.parentElement?.nodeName !== 'CAT-PRIMARY-NAV-ITEM') {
            newSubListElement = <BlocksListItem>newSubListElement.parentElement;

            if (newSubListElement.nodeName === 'CAT-LIST-ITEM') {
              newSubListElement.isCurrent = true; /* 5 */
            }
          }
        }
      }
      this.closePanel(); /* 3 */
      this._navLink?.focus();
    }
  }

  /**
   * function which will emit bl-nav-select. Can be prevented to apply custom handler
   * @returns boolean
   */
  private emitNavSelect() {
    const activeEvent = this.dispatch({
      eventName: 'bl-nav-select',
      detailObj: { isMobile: this.isMobile },
      optionsObj: { cancelable: true }
    }) as BlocksNavSelectEvent;
    return activeEvent.defaultPrevented;
  }

  /**
   * Here we will check whether any other primary nav item is selected or not.
   * if another item is selected then remove all `isCurrent` states from those items.
   */
  private checkPrimaryNavItems() {
    const allNavPrimaryItems = this.parentElement?.querySelector('slot')?.assignedElements() as BlocksPrimaryNavItem[];
    allNavPrimaryItems.forEach((item: BlocksPrimaryNavItem) => {
      item.isCurrent = false;
    });
  }

  /**
   * Update the current list item with level and id information for stepped menu recursively
   * @param children - current children list instance
   * @param level - level information starting with 0
   */
  private updateNodeWithCustomAttribute(listItems: HTMLCollection, level: number) {
    for (const listItem of Array.from(listItems)) {
      listItem.setAttribute('data-level', level.toString());
      listItem.setAttribute('data-listId', nanoid());
      const slot = listItem?.shadowRoot?.querySelector<HTMLSlotElement>('slot[name="items"]');

      const assignedNodes = slot?.assignedNodes({ flatten: true });

      const hasSlottedContent = !!assignedNodes?.length;

      for (const list of Array.from(listItem.children)) {
        if (hasSlottedContent) {
          // setting default list item behavior to mega menu flyout
          const currentListItem = listItem as BlocksListItem;
          currentListItem.isMegaMenuFlyout = !this.isMobile;
          currentListItem.isMegaMenuInMobileView = this.isMobile;
          // setting default list behavior to stepped mega menu
          const currentList = list as BlocksList;
          currentList.isSteppedMegaMenu = !this.isMobile;
          this.updateNodeWithCustomAttribute(list.children, level + 1);
        }
      }
      this.setListItemPadding(listItem, level);
    }
  }

  /**
   *In List-item component explicitly for 3rd level inline start has been set to 32 px to change it to 16px in case of steppedMegamenu desktop view.
   */
  private setListItemPadding(listItem: Element, level: number) {
    if (level >= 2 && !this.isMobile) {
      const item = listItem as BlocksListItem;
      item.style.setProperty('--cat-list-item-link-padding-inline-start', '16px');
    }
  }

  /**
   * Handle overflow on PrimaryNav
   * 1) If the dropdown panel or megamenu panel is open, emit this event
   */
  private panelOpenEvent() {
    this.dispatch({
      eventName: 'bl-primarynav-item-panel-open'
    });
  }

  /**
   * Handle overflow on PrimaryNav
   * 1) If the dropdown panel or megamenu panel is close, emit this event
   * 2) If stepped menu reset panel width and close all tiers
   * 3) isFocusedIn set to false so that when user clicks can focus again
   */
  private panelCloseEvent() {
    this.dispatch({
      eventName: 'bl-primarynav-item-panel-close'
    });
    this.resetSteppedMenuPanelWidth();
    this.isFocusedIn = false;
  }

  /**
   * Used to push the element to item list for stepped menu
   * @param element element information to push
   */
  private pushTrackingItem(element: { id: string; level: string; width: number; label: string }): void {
    // push element into the list items
    this.items.push(element);
  }

  /**
   * Remove element from tracking item list for stepped menu
   * @param id element id
   */
  private removeTrackingElement(id: string): void {
    this.items = this.items.filter((item: { id: string; level: string; width: number }) => item.id !== id);
  }

  /**
   * Reset panel width to default
   */
  private resetSteppedMenuPanelWidth() {
    if (this.variant === 'steppedmegamenu') {
      this._megamenuPanel.style.width = 'auto';

      if (!this.isMobile) {
        this.items = [];
        const listItems = this.getCatListItems;
        listItems.forEach((item: BlocksListItem) => {
          if (item.isActive) {
            item.isActive = false;
          }
        });
      }
    }
  }

  /**
   * Gets the first list related list items
   * @returns list items
   */
  private getFirstListItems(): HTMLCollection | undefined {
    return this.children.length > 0 ? this.children[0].children : undefined;
  }

  /**
   * Update child List elements with data - level and id information for list items
   **/
  private updateListForSteppedMegamenu() {
    if (this.variant === 'steppedmegamenu') {
      const firstListItems = this.getFirstListItems();

      if (firstListItems) {
        const currentList = this.children[0] as BlocksList;
        currentList.isSteppedMegaMenu = !this.isMobile;
        this.updateNodeWithCustomAttribute(firstListItems, 0);
      }
    }
  }

  /**
   * @param componentClassName - style classes
   * @returns - mega menu html
   */
  private renderMegaMenu(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    this.updateListForSteppedMegamenu();

    return html`
      <li part="base" role="none" class="${componentClassName}" @keydown=${this.handleOnMegaMenuKeyDown}>
        <button
          part="link"
          role="menuitem"
          aria-haspopup="true"
          class="cat-c-primary-nav__link"
          @click=${this.clickHandler}
          aria-expanded=${this.isActive === true ? true : false}
          aria-current=${ifDefined(this.isCurrent === true ? 'page' : undefined)}
        >
          <div part="icon-before" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-before' : 'cat-c-primary-nav__icon'}>
            <slot part="part-before" name="before"></slot>
          </div>
          <span class="cat-c-primary-nav__text">${this.text}</span>
          <div part="icon-after" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-after' : 'cat-c-primary-nav__icon'}>
            <slot part="part-after" name="after"></slot>
          </div>
          <slot part="part-icon" name="icon">
            <cat-icon-chevron-down part="icon-down" exportparts=${this.getExportParts('chevron-down', 'icon')}></cat-icon-chevron-down>
          </slot>
        </button>
        <div part="panel" class="cat-c-primary-nav__item-panel">
          <div part="panel-inner" class="cat-c-primary-nav__item-panel-inner">
            ${this.variant === 'steppedmegamenu' && !this.isMobile
              ? html`<div class="cat-c-wrapper">
                  <cat-dropdown-panel part="dropdown-panel" exportparts=${this.getExportParts('dropdown-panel')}>
                    <slot part="part-default" @click=${this.itemSelected} @slotchange=${this.setRoles}></slot>
                  </cat-dropdown-panel>
                </div>`
              : html`<slot part="part-default" @click=${this.itemSelected} @slotchange=${this.setRoles}></slot>`}
          </div>
        </div>
        <div class="cat-c-primary-nav__bottom-border"></div>
      </li>
    `;
  }

  /**
   * @param componentClassName - style classes
   * @returns - dropdown html
   */
  private renderDropdown(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <li part="base" role="none" class="${componentClassName}" @keydown=${this.handleOnDropdownKeyDown}>
        <button
          part="link"
          role="menuitem"
          aria-haspopup="true"
          class="cat-c-primary-nav__link"
          @click=${this.clickHandler}
          aria-expanded=${this.isActive === true ? true : false}
          aria-current=${ifDefined(this.isCurrent === true ? 'page' : undefined)}
        >
          <div part="icon-before" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-before' : 'cat-c-primary-nav__icon'}>
            <slot part="part-before" name="before"></slot>
          </div>
          <span class="cat-c-primary-nav__text">${this.text}</span>
          <div part="icon-after" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-after' : 'cat-c-primary-nav__icon'}>
            <slot part="part-after" name="after"></slot>
          </div>
          <slot part="part-icon" name="icon">
            <cat-icon-chevron-down part="icon-down" exportparts=${this.getExportParts('chevron-down', 'icon')}></cat-icon-chevron-down>
          </slot>
        </button>
        <div part="panel" class="cat-c-primary-nav__item-panel">
          <div part="panel-inner" class="cat-c-primary-nav__item-panel-inner">
            <slot part="part-default" @click=${this.itemSelected} @slotchange=${this.setRoles}></slot>
          </div>
        </div>
        <div class="cat-c-primary-nav__bottom-border"></div>
      </li>
    `;
  }

  /**
   * @param componentClassName - style classes
   * @returns - primary nav link html
   */
  private renderNavLink(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <li part="base" role="none" class="${componentClassName}" @keydown=${this.handleOnKeyDown}>
        <a
          part="link"
          class="cat-c-primary-nav__link"
          @click=${this.linkClickHandler}
          href="${ifDefined(this.href)}"
          target="${ifDefined(this.target)}"
          role="menuitem"
          aria-current=${ifDefined(this.isCurrent === true ? 'page' : undefined)}
        >
          <div part="icon-before" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-before' : 'cat-c-primary-nav__icon'}>
            <slot part="part-before" name="before"></slot>
          </div>
          <span class="cat-c-primary-nav__text">${this.text}</span>
          <div part="icon-after" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-after' : 'cat-c-primary-nav__icon'}>
            <slot part="part-after" name="after"></slot>
          </div>
        </a>
        <div class="cat-c-primary-nav__bottom-border"></div>
      </li>
    `;
  }

  /**
   * @param componentClassName - style classes
   * @returns - primary nav custom-link html
   */
  private renderCustomLink(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <li part="base" role="none" class="${componentClassName}" @click=${this.linkClickHandler} @keydown=${this.handleKeyDown}>
        <div part="link" class="cat-c-primary-nav__link" tabindex=${0} role="menuitem">
          <slot part="part-custom-link" name="custom-link" tabindex=${-1}></slot>
        </div>
        <div class="cat-c-primary-nav__bottom-border"></div>
      </li>
    `;
  }

  /**
   * @param componentClassName - style classes
   * @returns - primary nav button html
   */
  private renderNavButton(componentClassName: DirectiveResult<typeof ClassMapDirective>) {
    return html`
      <li part="base" role="none" class="${componentClassName}" @keydown=${this.handleOnKeyDown}>
        <button
          part="link"
          role="menuitem"
          class="cat-c-primary-nav__link"
          @click=${this.linkClickHandler}
          href="${ifDefined(this.href)}"
          target="${ifDefined(this.target)}"
          aria-current=${ifDefined(this.isCurrent ? 'page' : undefined)}
        >
          <div part="icon-before" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-before' : 'cat-c-primary-nav__icon'}>
            <slot part="part-before" name="before"></slot>
          </div>
          <span class="cat-c-primary-nav__text">${this.text}</span>
          <div part="icon-after" class=${this.text ? 'cat-c-primary-nav__icon cat-c-primary-nav__icon-after' : 'cat-c-primary-nav__icon'}>
            <slot part="part-after" name="after"></slot>
          </div>
        </button>
        <div class="cat-c-primary-nav__bottom-border"></div>
      </li>
    `;
  }

  render(): TemplateResult<1> {
    const componentClassName = this.componentClassNames('cat-c-primary-nav__item', {
      'cat-is-active': this.isActive === true,
      'cat-is-current': this.isCurrent === true,
      'cat-c-primary-nav__item--megamenu': this.megaMenu === true,
      'cat-c-primary-nav__item--steppedmegamenu': this.variant === 'steppedmegamenu',
      'cat-c-primary-nav__item--dropdown': this.dropdown === true,
      'cat-c-primary-nav__item--more-dropdown': this.more === true,
      'cat-c-primary-nav__item--dropdown-align-left': this.dropdownAlign === 'left',
      'cat-is-mobile': this.isMobile === true,
      'cat-c-primary-nav__item--has-slot-before': this.slots.hasSlotted('before'),
      'cat-c-primary-nav__item--has-slot-after': this.slots.hasSlotted('after')
    });

    if (this.megaMenu) {
      return this.renderMegaMenu(componentClassName);
    }

    if (this.dropdown) {
      return this.renderDropdown(componentClassName);
    }

    if (this.href) {
      return this.renderNavLink(componentClassName);
    }

    if (this.hasCustomLinkSlot) {
      return this.renderCustomLink(componentClassName);
    }

    return this.renderNavButton(componentClassName);
  }
}

if (customElements.get('cat-primary-nav-item') === undefined) {
  customElements.define('cat-primary-nav-item', BlocksPrimaryNavItem);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-primary-nav-item': BlocksPrimaryNavItem;
  }
}
